/**
	This function is used to display possible rental cars and airport transfer in a modal.
	When the tab is out of focus for 5 seconds, the modal will open.

	Tracking: data-value is used to index which exit user selects.
	0 		= yellow button
	1 		= big car
	2 etc = alternative cars
**/
import { EventDispatcher, addEventListeners, getCookie, getUrlVars, isNetworkError, logEvent, qs, qsa } from '@utils/toolbox';
import { body, footer } from './templates';
import { Civet } from '@js/plugins/civet';
import { Modal } from '@js/components/modal';
import { eventCategories, events } from '@js/utils/event';

export const KalleBakom = (opts) => {
	let modal;
	let worker;
	let activeRequest = false;
	let messageEventHandler = () => {};
	const debug = getUrlVars()?.debugKalle;

	const defaults = {
		onBlurEnabled: true,
		onBlurDelay: debug ? 0 : js_params.kalleBakomDelay,
		cancelOnFocusEnabled: debug ? false : true
	};

	const options = {
		...defaults,
		...opts
	};

	function blurHandler (kallebakomRequest) {
		return function () {
			console.log('Window blurred');
			if (!activeRequest) {
				activeRequest = true;
				if (debug && debug !== 'true') {
					kallebakomRequest.push(debug);
				}
				worker.postMessage(JSON.stringify({
					url: `${window.location.protocol}//${window.location.hostname}/conapi`,
					request: kallebakomRequest,
					action: 'start',
					delay: options.onBlurDelay
				}));
			}
		};
	}

	function focusEventHandler () {
		console.log('Window focused back', activeRequest);
		if (activeRequest) {
			worker.postMessage(JSON.stringify({
				action: 'cancel'
			}));
			activeRequest = false;
		}
	}

	let blurEventHandler;

	function display ({ airportInformation, kallebakomRequest }) {
		if (options.onBlurEnabled && 'serviceWorker' in navigator) {
			if (!worker) {
				worker = new Worker(`/static/v${js_params.version}/assets/resources/build/js/plugins/kalleBakomServiceWorker.js`);
			}

			blurEventHandler = blurHandler(kallebakomRequest);
			window.addEventListener('blur', blurEventHandler);

			// If Cypress is enabled, we want to trigger the blur event manually
			if (getCookie('CYPRESS_ENABLED') || debug) {
				blurEventHandler(kallebakomRequest);
			}

			messageEventHandler = (event) => {
				activeRequest = false;
				const data = JSON.parse(event.data);
				switch (data.action) {
					case 'fetchComplete': {
						const kallebakomOffers = data.payload;

						if (kallebakomOffers.length) {
							// Creates the modal
							createModal(airportInformation, kallebakomOffers);
							modal.open();
							EventDispatcher.dispatch('kalleBakom:modalOpened', { kallebakomOffers });

							let wasFocused = false;
							window.addEventListener('focus', () => {
								if (qs('#kallebakomModal') && qs('.kallebakom__item.active') && !wasFocused) {
									logEvent(events.observeKallebakom, eventCategories.observe);
									wasFocused = true;
								}
							});
						} else {
							console.warn('No kalle offers');
						}

						break;
					}
					case 'fetchError':
						if (!isNetworkError(data.error)) {
							Civet.reportError(data.error);
						}
						break;
				}
				removeEventListeners();
			};

			// Listen for response when the worker is done
			worker.addEventListener('message', messageEventHandler);
			if (options.cancelOnFocusEnabled) {
				window.addEventListener('focus', focusEventHandler);
			}
		}
	}

	function removeEventListeners () {
		window.removeEventListener('blur', blurEventHandler);
		window.removeEventListener('focus', focusEventHandler);
		worker?.removeEventListener?.('message', messageEventHandler);
	}

	function destroy () {
		removeEventListeners();
		activeRequest = false;
		modal.destroy();
	}

	/**
	 * @param {*} airportInformation
	 * @param {Item[]} kallebakomOffers 	different offers
	 */
	const createModal = (airportInformation, kallebakomOffers) => {
		let footerContent = '';
		// let headerContent = '';
		let offerId = '';

		document.body.classList.add('testingV2');

		const selectedOffer = kallebakomOffers.find(offer => offer.selected);

		if (!selectedOffer) {
			console.warn('No selected offer');
			return;
		}

		footerContent = footer(selectedOffer.id, selectedOffer.forwardURL);
		// headerContent = header(selectedOffer.id, kallebakomOffers);
		offerId = selectedOffer.id;

		// let hideheader = true;
		// if (document.body.classList.contains('testingV2')) {
		// 	hideheader = false;
		// }

		const analyticsEventMap = {
			bookingcom: events.kalleBakomClickBooking,
			rentalcar: events.kalleBakomClickRentalCar,
			airporttransfer: events.kalleBakomClickAirportTransfer,
			forex: events.kalleBakomClickForex,
			arlandaexpress: events.kalleBakomClickArlandaExpress,
			airalo: events.kalleBakomClickAiralo
		};

		modal = new Modal({
			id: 'kallebakomModal',
			className: `kallebakom offers${kallebakomOffers.length} ${offerId}`,
			hideFooter: false,
			hideHeader: true,
			hideOnSwipe: false,
			resetScroll: true,
			footer: footerContent,
			// header: headerContent,
			body: body(airportInformation, kallebakomOffers),
			onOpen: () => {
				addEventListeners(qsa('.kallebakom_cta'), 'click', (e) => {
					if (e.target.getAttribute('data-url')) {
						const analyticsEvent = analyticsEventMap[e.target.getAttribute('data-kalleid')];
						logEvent(analyticsEvent, eventCategories.kallebakom, { target: 'model-cta' });
						window.open(e.target.getAttribute('data-url'), '_blank');
					} else {
						console.warn('No url found');
					}
				});
				addEventListeners(qsa('.link-item'), 'click', (e) => {
					const analyticsEvent = analyticsEventMap[e.target.closest('a').getAttribute('data-kalleid')];
					logEvent(analyticsEvent, eventCategories.kallebakom, { target: 'extra-item' });
				});
			},
			onClose: () => {
				EventDispatcher.dispatch('kalleBakom:modalClosed');
				destroy();
			},
			...options.modalOptions
		});
	};

	return {
		display,
		destroy
	};
};
